/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useContext } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useSelector } from 'react-redux';
import { object, string, PropTypes, arrayOf, bool, number } from 'prop-types';
import mbpLogger from 'mbp-logger';
import { useQueryClient } from 'react-query';

// MUI
import {
    Grid, Button, CircularProgress,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { joiResolver } from '@hookform/resolvers/joi';

// Component
import RecipientAvatar from '../AddNewRecipient/AddProfile/RecipientAvatar';
// import EditRecipientAvatarModal from '../AddNewRecipient/AddProfile/EditRecipientAvatarModal';
import AddProfileForm from '../AddNewRecipient/AddProfile/AddProfileForm';

// Helpers
import { schemaForEditProfile } from '../Helpers/Schema';
import { getUserProfile } from '../../../../../../../../state/ducks/Checkout/ducks/Common/Common-Selectors';
import { getAccessTokenSafely } from '../../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';
import { formatNameToProperCapitalization } from '../Helpers/Helpers';
import { RecipientBookContext } from '../Context/RecipientBookContext';
import CUSTOM_BREAKPOINTS from '../../../../../style/customBreakpoints';

// Icons
import closeIcon from '../Assets/filterCloseIcon.svg';

// APIs
// import updateSingleAddressEntry from '../../../../../../../../apis/account-apis/RecipientBook-apis/updateSingleAddressEntry';
import processUpdateRecipientProfile from '../../../../../../../../apis/account-apis/RecipientBook-apis/processUpdateRecipientProfile';
// import StyledButton from '../../../../../../../components/EnterpriseDesignSystem/Button/StyledButton';

const useStyles = makeStyles((theme) => ({
    editProfileWrapper: {
        marginTop: '10px',
        marginBottom: '16px',
        [theme.breakpoints.up(CUSTOM_BREAKPOINTS.tabLandscape)]: {
            marginBottom: '24px',
        },
    },
    recipientInfo: {
        paddingLeft: '10px',
        maxWidth: '208px',
        [theme.breakpoints.up(CUSTOM_BREAKPOINTS.tabLandscape)]: {
            maxWidth: '560px',
        },
        '& p': {
            marginBottom: '0px !important',
            marginTop: '0px !important',
            wordBreak: 'break-word',
            [theme.breakpoints.up(CUSTOM_BREAKPOINTS.tabLandscape)]: {
                wordBreak: 'auto-phrase',
            },
        },
        '& .name': {
            fontFamily: 'Lato',
            fontSize: '24px !important',
            fontStyle: 'normal',
            fontWeight: 700,
            lineHeight: '36px', /* 150% */
            letterSpacing: '0.32px',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            [theme.breakpoints.up(CUSTOM_BREAKPOINTS.tabLandscape)]: {
                fontSize: '32px !important',
                lineHeight: '48px', /* 150% */
            },
        },
    },
    profileInfo: {
        textAlign: 'end', 
        alignSelf: 'center'
    },
    profileBtn: {
        borderRadius: '0px', 
        cursor: 'pointer'
    },
    w100: {
        width: '100%'
    },
    error: {
        color: 'red'
    },
    gridBtn: {
        height: '46px !important', 
        margin: '0px 0px 0px 24px!important', 
        width: '244px !important',
    }
}));

const EditProfile = ({ classes, recipient, recipientRelationship, parentComponent }) => {
    const queryClient = useQueryClient();
    const { setCurrentRecipient, currentRecipient, setIsProfileEditing, isProfileEditing,
        setIsOccasionsEditing, setIsAddressEditing, setSelectedOccasionToEdit, setSelectedAddressToEdit, setAddNewAddressClicked,
    } = useContext(RecipientBookContext);

    const methods = useForm({ mode: 'all', resolver: joiResolver(schemaForEditProfile) });
    const localClasses = useStyles();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [recipientFirstInitial, setRecipientFirstInitial] = useState(recipient.rcpFirstName?.[0] || recipient.rcpEmployerName?.[0] || '');
    const [recipientLastInitial, setRecipientLastInitial] = useState(recipient.rcpLastName?.[0] || '');

    const userProfile = useSelector(getUserProfile) || {};
    const { ContactId } = userProfile || '';
    const relationshipTypes = queryClient.getQueryData(['relationships'])?.byValue;

    const processUpdateProfile = useMutation({
        mutationFn: processUpdateRecipientProfile,
        onSuccess: (data) => {
            if (data && data.data !== undefined) {
                const { addressResponse } = data.data;
                const updateRecipient = () => {
                    // Create a copy of the recipient data
                    // const updatedRecipientData = { ...recipientState };
                    // Loop through the addressList array
                    let currentRecip = {};
                    if (currentRecipient.rcpid === addressResponse.rcpid) {
                        currentRecip = {
                            ...currentRecipient,
                            ...addressResponse,
                            rcpEmail: addressResponse.rcpEmail || '',
                            rcpPhone: addressResponse.rcpPhone || '',
                            relationship: addressResponse.relationship || '',
                            rcpEmployerName: addressResponse.rcpEmployerName || '',
                        }
                    }
                    if (currentRecip) setCurrentRecipient(currentRecip);
                };
                updateRecipient();
            }
            setLoading(false);
            setError('');
            setTimeout(() => methods.reset(), 500);
            setIsProfileEditing(false);
        },
        onError: (err) => {
            setError('Something went wrong. Please try again.');
            setLoading(false);
            mbpLogger.logError({
                function: 'processUpdateProfileMutation',
                appName: process.env.npm_package_name,
                module: 'EditProfile.js',
                message: `Error While Updating a Recipient with Contact ID: ${ContactId}`,
                err,
            });
        },
    });

    const updateProfile = async (payload) => {
        setLoading(true);
        const jwtToken = await getAccessTokenSafely();
        processUpdateProfile.mutate({
            ContactId,
            rcpId: recipient?.rcpId,
            data: payload,
            jwtToken,
        });
    };

    const onSubmit = (data) => {
        const { rcpFirstName, rcpLastName, rcpEmployerName, relationship, rcpEmail, rcpEmailType, rcpPhone, rcpPhoneType } = data;
        if (+relationshipTypes[relationship] === 0) {
            currentRecipient.isSelfRecipientAvailable = relationshipTypes[relationship] === 0;
        }

        if (+currentRecipient.relationship === 0) {
            const addressBookResponse = queryClient.getQueryData(['recipientBookList', userProfile?.ContactId])?.data?.addressBookResponse;
            currentRecipient.isSelfRecipientAvailable = addressBookResponse?.addressList?.find(address => +address.relationship === 0 && currentRecipient.rcpId !== address.rcpId) ? true : false;
        }
        // const formattedFirstAndLastName = formatNameToProperCapitalization(`${rcpFirstName.replace(/\s/g, '')} ${rcpLastName.replace(/\s/g, '')}`).split(' '); // firstname and lastname should not contains spaces otherwise it will take firstname's 2nd element as a last name
        // const [formattedFirstName, formattedLastName] = [formattedFirstAndLastName[0], formattedFirstAndLastName[1]];
        const payload = {
            relationship: relationshipTypes[relationship] || '',
            rcpId: recipient.rcpId,
            rcpFirstName,
            rcpLastName,
            rcpEmployerName,
            rcpEmail,
            rcpEmailType,
            rcpPhoneType,
            rcpPhone,
            createdUser: 'SSP',
        };

        const isProfileModified = Object.keys(methods.formState.dirtyFields).filter((key) => {
            if (key === 'relationship') {
                return data[key] !== recipientRelationship;
            }
            return data[key] !== recipient[key]?.length;
        });

        if (isProfileModified) {
            updateProfile(payload);
        } else {
            setError('Please modify profile details to submit.');
        }
    };

    const handleEditProfile = () => {
        methods.reset();
        setSelectedOccasionToEdit('')
        setSelectedAddressToEdit('')
        setAddNewAddressClicked(false);
        setIsProfileEditing(true); // setting global profile state true to keep edit profile form open
        setIsOccasionsEditing(false); // setting global occasion state false to keep close all other edit screens
        setIsAddressEditing(false); // setting global occasion state false to keep close all other edit screens
    };

    const getRecipNameOrCompanyName = () => {
        let name = '';
        if (!(recipient.rcpFirstName && recipient.rcpLastName) && recipient.rcpEmployerName) {
            name = formatNameToProperCapitalization(recipient.rcpEmployerName);
        } else if (recipient.rcpFirstName && recipient.rcpLastName) {
            name = formatNameToProperCapitalization(`${recipient.rcpFirstName} ${recipient.rcpLastName}`);
        }
        return name;
    };

    return (
        <Grid container item className={classes.addNewRecipientWrapper}>
            <Grid container item xs={12} className={localClasses.editProfileWrapper}>
                {/* <Grid container> */}
                <Grid container item xs={10} alignItems='center'>
                    <Grid item>
                        <RecipientAvatar
                            width={73}
                            height={70}
                            // onClick={() => setIsAvatarModalOpen(true)}
                            recipientFirstInitial={recipientFirstInitial}
                            recipientLastInitial={recipientLastInitial}
                        />
                    </Grid>
                    <Grid item className={localClasses.recipientInfo}>
                        {/* technical debt - instead of formatting the name on the front end it should be processed to proper capitalization when sending to api */}
                        <p className="name">{getRecipNameOrCompanyName()}</p>
                        <p>{recipientRelationship}</p>
                    </Grid>
                </Grid>
                <Grid item xs={2} className={localClasses.profileInfo}>
                    {!isProfileEditing
                        ? (<button type="button" className={`${classes.editBtn} ${localClasses.profileBtn}`} onClick={handleEditProfile}>Edit</button>)
                        : (<div className={classes.closeIcon} role="button" tabIndex={0} onClick={() => {setIsProfileEditing(false)}} onKeyDown={() => { }}><span><img src={closeIcon} alt="close icon" /></span></div>)
                    }
                </Grid>
            </Grid>
            {isProfileEditing && (
                <Grid container item xs={12} className="formStyle" justifyContent='center'>
                    <form className={localClasses.w100} autoComplete='off'>
                        <AddProfileForm
                            methods={methods}
                            recipientProfile={{
                                ...recipient,
                                relationship: recipientRelationship,
                            }}
                            setRecipientFirstInitial={setRecipientFirstInitial}
                            setRecipientLastInitial={setRecipientLastInitial}
                            parentComponent={parentComponent}
                        />
                        {error
                            && (
                                <Grid container justifyContent="center" alignItems="center">
                                    <p className={localClasses.error}>{error}</p>
                                </Grid>
                            )}
                    </form>
                    <Grid container item xs={12} md={5} lg={5} justifyContent="center" alignItems="center">
                        <Button
                            variant='contained'
                            fullWidth
                            style={{ margin: '16px 0px 0px 0px', height: '46px', minWidth: '244px' }}
                            onClick={methods.handleSubmit(onSubmit)}
                            disabled={loading || !methods.formState.isDirty || Object.keys(methods.formState.errors).length > 0}
                            className={classes.commonBtnContained}
                        >
                            {loading ? <CircularProgress /> : 'Save'}
                        </Button>
                    </Grid>
                </Grid>
            )}
        </Grid>
    );
};

EditProfile.propTypes = {
    classes: object.isRequired,
    recipient: PropTypes.shape({
        addressBookResponse: PropTypes.shape({
            addressList: arrayOf(PropTypes.shape({
                addresses: arrayOf(PropTypes.shape({
                    addressBookEntryId: string,
                    addressLineOne: string,
                    addressType: string,
                    city: string,
                    country: string,
                    firstName: string,
                    isBillingAddress: bool,
                    lastName: string,
                    lastUpdateDate: string,
                    phoneNumber: string,
                    rcpId: string,
                    state: string,
                    zip: string,
                })),
                occasions: arrayOf(PropTypes.shape({
                    desc: string,
                    rcpId: string,
                    dateDD: string,
                    dateMM: string,
                    dateYYYY: string,
                    subscribed: bool,
                    occasion: string,
                    occasionId: string,
                    createdDate: string,
                    createdUser: string,
                    reminderEmail: string,
                    reminderPhone: string,
                    subscribedEmail: bool,
                    subscribedPhone: bool,
                })),
                rcpAddressBookEntryId: string,
                rcpFirstName: string,
                rcpId: string,
                rcpLastName: string,
                rcpPhone: string,
                rcpEmail: string,
            })),
            contactId: string,
            reminderEmail: string,
            totalEntries: number,
        }),
    }),
    recipientRelationship: string,
    parentComponent: string.isRequired,
};

export default EditProfile;
